import React, { useState, useEffect } from 'react';
import { useAuth, VIEW_MODES } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { formatCurrency, getCurrencySymbol } from '../lib/currency';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  PiggyBank,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  ChevronRight,
  CheckCircle2,
  Landmark,
  Users,
  Receipt,
  Calendar,
  DollarSign,
  Clock,
  BarChart3
} from 'lucide-react';
import { motion } from 'framer-motion';

// Animation wrapper
const FadeIn = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay, ease: 'easeOut' }}
    className={className}
  >
    {children}
  </motion.div>
);

// Large KPI card for CEO view
const BigKPICard = ({ title, value, delta, positive, icon: Icon, currency, loading, subtitle, onClick }) => {
  // Calculate responsive font size based on formatted value length
  const formattedValue = formatCurrency(value, currency);
  const valueLength = formattedValue.length;
  
  // Dynamic font size: shorter values get larger fonts
  const getFontSizeClass = () => {
    if (valueLength <= 10) return 'text-2xl sm:text-3xl';
    if (valueLength <= 14) return 'text-xl sm:text-2xl';
    if (valueLength <= 18) return 'text-lg sm:text-xl';
    return 'text-base sm:text-lg';
  };

  return (
    <Card 
      className={`bg-white/90 dark:bg-slate-900/90 backdrop-blur-lg border-0 shadow-xl hover:shadow-2xl transition-all duration-300 ${onClick ? 'cursor-pointer' : ''}`} 
      data-testid={`ceo-kpi-${title.toLowerCase().replace(/\s/g, '-')}`}
      onClick={onClick}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className={`p-3 rounded-xl flex-shrink-0 ${
            positive === true ? 'bg-emerald-100 text-emerald-600' :
            positive === false ? 'bg-rose-100 text-rose-600' :
            'bg-[hsl(var(--brand-100))] text-[hsl(var(--brand-600))]'
          }`}>
            <Icon size={24} />
          </div>
          {delta !== undefined && delta !== null && (
            <Badge className={`text-sm font-semibold px-2 py-0.5 flex-shrink-0 ${
              positive === true
                ? 'bg-emerald-100 text-emerald-700'
                : positive === false
                ? 'bg-rose-100 text-rose-700'
                : 'bg-slate-100 text-slate-600'
            }`}>
              {positive === true ? <ArrowUpRight size={14} className="mr-1" /> :
               positive === false ? <ArrowDownRight size={14} className="mr-1" /> :
               <Minus size={14} className="mr-1" />}
              {Math.abs(delta).toFixed(1)}%
            </Badge>
          )}
        </div>
        
        <div className="mb-1">
          <span className="text-xs font-medium text-[hsl(var(--muted-ink))] uppercase tracking-wide">{title}</span>
        </div>
        
        {loading ? (
          <Skeleton className="h-10 w-36" />
        ) : (
          <div className={`${getFontSizeClass()} font-bold numbered tracking-tight break-words`}>
            {formattedValue}
          </div>
        )}
        
        {subtitle && (
          <p className="text-xs text-[hsl(var(--muted-ink))] mt-1">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );
};

// Compact metric card
const MetricCard = ({ title, value, icon: Icon, color, currency, loading, onClick, badge }) => (
  <Card 
    className={`bg-white/80 backdrop-blur border-0 shadow-md hover:shadow-lg transition-all ${onClick ? 'cursor-pointer' : ''}`}
    onClick={onClick}
  >
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`p-2.5 rounded-lg ${color}`}>
        <Icon size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-[hsl(var(--muted-ink))] truncate">{title}</p>
        {loading ? (
          <Skeleton className="h-6 w-20" />
        ) : (
          <p className="text-lg font-bold numbered truncate">
            {currency ? formatCurrency(value, currency) : value}
          </p>
        )}
      </div>
      {badge && (
        <Badge className={badge.color}>{badge.text}</Badge>
      )}
    </CardContent>
  </Card>
);

// Action item card for CEO
const ActionItemCard = ({ item }) => (
  <div className={`p-3 rounded-lg border-l-4 ${
    item.urgency === 'high'
      ? 'bg-rose-50 border-rose-500 dark:bg-rose-900/20'
      : 'bg-amber-50 border-amber-500 dark:bg-amber-900/20'
  }`}>
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{item.title}</p>
        <p className="text-xs text-[hsl(var(--muted-ink))] mt-0.5 line-clamp-2">{item.description}</p>
      </div>
      <Badge className={`shrink-0 text-xs ${
        item.urgency === 'high' ? 'bg-rose-200 text-rose-800' : 'bg-amber-200 text-amber-800'
      }`}>
        {item.urgency === 'high' ? 'Urgent' : 'Soon'}
      </Badge>
    </div>
  </div>
);

const DashboardSimple = () => {
  const { company, currency } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [receivablesSummary, setReceivablesSummary] = useState(null);
  const [payablesSummary, setPayablesSummary] = useState(null);
  const [loanSummary, setLoanSummary] = useState(null);

  useEffect(() => {
    loadDashboard();
    
    const handleRefresh = () => loadDashboard();
    window.addEventListener('transaction-created', handleRefresh);
    return () => window.removeEventListener('transaction-created', handleRefresh);
  }, []);

  const loadDashboard = async () => {
    try {
      const [dashRes, recRes, payRes] = await Promise.all([
        api.get('/dashboard'),
        api.get('/receivables/summary').catch(() => ({ data: null })),
        api.get('/payables/summary').catch(() => ({ data: null }))
      ]);
      
      setDashboardData(dashRes.data);
      setReceivablesSummary(recRes.data);
      setPayablesSummary(payRes.data);
      setLoanSummary(dashRes.data?.loan_summary || null);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const kpis = dashboardData?.kpis || {};
  const greeting = new Date().getHours() < 12 ? 'Good morning' : new Date().getHours() < 18 ? 'Good afternoon' : 'Good evening';
  
  // Calculate financial health score (0-100)
  const profitMargin = kpis.revenue?.value > 0 ? (kpis.net_profit?.value / kpis.revenue?.value) * 100 : 0;
  const healthScore = Math.min(100, Math.max(0, 50 + profitMargin));
  
  // Calculate net position
  const receivablesTotal = receivablesSummary?.total_outstanding || 0;
  const payablesTotal = payablesSummary?.total_outstanding || 0;
  const loansTotal = loanSummary?.total_outstanding || 0;
  const netPosition = (kpis.cash_position?.value || 0) + receivablesTotal - payablesTotal - loansTotal;

  return (
    <div className="space-y-6 pb-8" data-testid="dashboard-simple">
      {/* Hero greeting */}
      <FadeIn>
        <div className="text-center py-4">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            {greeting}, {company?.name?.split(' ')[0] || 'there'}
          </h1>
          <p className="text-sm text-[hsl(var(--muted-ink))] mt-1">
            Here's your business at a glance
          </p>
        </div>
      </FadeIn>

      {/* Main KPIs Row */}
      <FadeIn delay={0.05}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <BigKPICard
            title="Revenue"
            value={kpis.revenue?.value || 0}
            delta={kpis.revenue?.delta}
            positive={kpis.revenue?.positive}
            icon={TrendingUp}
            currency={currency}
            loading={loading}
            subtitle="This month"
            onClick={() => navigate('/transactions')}
          />
          <BigKPICard
            title="Expenses"
            value={kpis.expenses?.value || 0}
            delta={kpis.expenses?.delta}
            positive={kpis.expenses?.positive}
            icon={TrendingDown}
            currency={currency}
            loading={loading}
            subtitle="This month"
            onClick={() => navigate('/transactions')}
          />
          <BigKPICard
            title="Net Profit"
            value={kpis.net_profit?.value || 0}
            positive={kpis.net_profit?.positive}
            icon={PiggyBank}
            currency={currency}
            loading={loading}
            subtitle={kpis.net_profit?.positive ? 'Profitable' : 'Review costs'}
            onClick={() => navigate('/reports')}
          />
          <BigKPICard
            title="Cash Position"
            value={kpis.cash_position?.value || 0}
            icon={Wallet}
            currency={currency}
            loading={loading}
            subtitle="Available now"
          />
        </div>
      </FadeIn>

      {/* Financial Health & Net Position */}
      <FadeIn delay={0.1}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Financial Health Score */}
          <Card className="bg-gradient-to-br from-[hsl(var(--brand-50))] to-white border-0 shadow-lg">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-sm">Financial Health</h3>
                  <p className="text-xs text-[hsl(var(--muted-ink))]">Based on profit margin</p>
                </div>
                <div className={`text-2xl font-bold ${
                  healthScore >= 70 ? 'text-emerald-600' : 
                  healthScore >= 40 ? 'text-amber-600' : 'text-rose-600'
                }`}>
                  {loading ? '-' : `${Math.round(healthScore)}`}
                </div>
              </div>
              <Progress 
                value={loading ? 0 : healthScore} 
                className={`h-2 ${
                  healthScore >= 70 ? '[&>div]:bg-emerald-500' : 
                  healthScore >= 40 ? '[&>div]:bg-amber-500' : '[&>div]:bg-rose-500'
                }`}
              />
              <div className="flex justify-between mt-2 text-xs text-[hsl(var(--muted-ink))]">
                <span>Needs Attention</span>
                <span>Excellent</span>
              </div>
            </CardContent>
          </Card>

          {/* Net Position */}
          <Card className="bg-white/90 border-0 shadow-lg">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-sm">Net Position</h3>
                  <p className="text-xs text-[hsl(var(--muted-ink))]">Cash + Receivables - Payables - Loans</p>
                </div>
                <BarChart3 size={20} className="text-[hsl(var(--brand-500))]" />
              </div>
              {loading ? (
                <Skeleton className="h-8 w-32" />
              ) : (
                <div className={`text-2xl font-bold ${netPosition >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {formatCurrency(netPosition, currency)}
                </div>
              )}
              <div className="grid grid-cols-4 gap-2 mt-3 text-center">
                <div>
                  <p className="text-xs text-[hsl(var(--muted-ink))]">Cash</p>
                  <p className="text-xs font-semibold text-emerald-600">
                    +{formatCurrency(kpis.cash_position?.value || 0, currency)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[hsl(var(--muted-ink))]">Owed to you</p>
                  <p className="text-xs font-semibold text-blue-600">
                    +{formatCurrency(receivablesTotal, currency)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[hsl(var(--muted-ink))]">You owe</p>
                  <p className="text-xs font-semibold text-amber-600">
                    -{formatCurrency(payablesTotal, currency)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[hsl(var(--muted-ink))]">Loans</p>
                  <p className="text-xs font-semibold text-rose-600">
                    -{formatCurrency(loansTotal, currency)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </FadeIn>

      {/* Receivables, Payables, Loans Row */}
      <FadeIn delay={0.15}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MetricCard
            title="Receivables"
            value={receivablesTotal}
            icon={Users}
            color="bg-blue-100 text-blue-600"
            currency={currency}
            loading={loading}
            onClick={() => navigate('/receivables')}
            badge={receivablesSummary?.overdue_count > 0 ? {
              text: `${receivablesSummary.overdue_count} overdue`,
              color: 'bg-rose-100 text-rose-700'
            } : null}
          />
          <MetricCard
            title="Payables"
            value={payablesTotal}
            icon={Receipt}
            color="bg-amber-100 text-amber-600"
            currency={currency}
            loading={loading}
            onClick={() => navigate('/receivables')}
            badge={payablesSummary?.overdue_count > 0 ? {
              text: `${payablesSummary.overdue_count} overdue`,
              color: 'bg-rose-100 text-rose-700'
            } : null}
          />
          <MetricCard
            title="Loans Outstanding"
            value={loansTotal}
            icon={Landmark}
            color="bg-purple-100 text-purple-600"
            currency={currency}
            loading={loading}
            onClick={() => navigate('/loans')}
            badge={loanSummary?.active_loans > 0 ? {
              text: `${loanSummary.active_loans} active`,
              color: 'bg-purple-100 text-purple-700'
            } : null}
          />
        </div>
      </FadeIn>

      {/* Period Summary & Action Items */}
      <FadeIn delay={0.2}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Period Summary */}
          <Card className="bg-white/90 border-0 shadow-lg">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Calendar size={18} className="text-[hsl(var(--brand-500))]" />
                This Month Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <TrendingUp size={16} className="text-emerald-600" />
                      <span className="text-sm">Total Income</span>
                    </div>
                    <span className="font-bold text-emerald-600">
                      {formatCurrency(kpis.revenue?.value || 0, currency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-rose-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <TrendingDown size={16} className="text-rose-600" />
                      <span className="text-sm">Total Expenses</span>
                    </div>
                    <span className="font-bold text-rose-600">
                      {formatCurrency(kpis.expenses?.value || 0, currency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-[hsl(var(--brand-50))] rounded-lg">
                    <div className="flex items-center gap-2">
                      <PiggyBank size={16} className="text-[hsl(var(--brand-600))]" />
                      <span className="text-sm">Net Result</span>
                    </div>
                    <span className={`font-bold ${(kpis.net_profit?.value || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {formatCurrency(kpis.net_profit?.value || 0, currency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Receipt size={16} className="text-slate-600" />
                      <span className="text-sm">Transactions</span>
                    </div>
                    <span className="font-bold text-slate-700">
                      {dashboardData?.transaction_count || 0}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Items */}
          <Card className="bg-white/90 border-0 shadow-lg">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertCircle size={18} className="text-amber-500" />
                Needs Attention
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : dashboardData?.action_items?.length > 0 ? (
                <div className="space-y-2">
                  {dashboardData.action_items.slice(0, 4).map((item, index) => (
                    <ActionItemCard key={index} item={item} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="w-12 h-12 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                    <CheckCircle2 size={24} className="text-emerald-600" />
                  </div>
                  <p className="font-medium text-sm">All caught up!</p>
                  <p className="text-xs text-[hsl(var(--muted-ink))]">No urgent items</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </FadeIn>

      {/* Quick Actions */}
      <FadeIn delay={0.25}>
        <div className="flex flex-wrap justify-center gap-3">
          <Button 
            variant="outline" 
            className="rounded-full px-5 text-sm"
            onClick={() => navigate('/reports')}
          >
            <BarChart3 size={16} className="mr-2" />
            View Reports
          </Button>
          <Button 
            variant="outline" 
            className="rounded-full px-5 text-sm"
            onClick={() => navigate('/transactions')}
          >
            <Receipt size={16} className="mr-2" />
            Transactions
          </Button>
          <Button 
            variant="outline" 
            className="rounded-full px-5 text-sm"
            onClick={() => navigate('/loans')}
          >
            <Landmark size={16} className="mr-2" />
            Loans
          </Button>
        </div>
      </FadeIn>
    </div>
  );
};

export default DashboardSimple;
